"use client";

import * as React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { SignOutIcon } from "@phosphor-icons/react/dist/ssr/SignOut";
import { UserIcon } from "@phosphor-icons/react/dist/ssr/User";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@apal/shared-auth";
import { UserAvatar, RoleBadge } from "@apal/shared-ui";
import { paths } from "@/paths";
import { RouterLink } from "@/components/core/link";

export interface UserPopoverProps {
	anchorEl: HTMLButtonElement | null;
	onClose: () => void;
	open: boolean;
}

export function UserPopover({ anchorEl, onClose, open }: UserPopoverProps): React.JSX.Element {
	const { user, logout } = useAuth();
	const navigate = useNavigate();

	const handleSignOut = React.useCallback(async () => {
		try {
			await logout();
			navigate(paths.login);
		} catch (error) {
			console.error("Sign out error:", error);
		}
	}, [logout, navigate]);

	const hasTier =
		user != null &&
		user.tierLevel != null &&
		user.tierLevel >= 1 &&
		user.tierLevel <= 5;
	const tier = hasTier ? `T${user.tierLevel}` : null;

	return (
		<Popover
			anchorEl={anchorEl}
			anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
			onClose={onClose}
			open={open}
			slotProps={{ paper: { sx: { width: "280px" } } }}
			transformOrigin={{ horizontal: "right", vertical: "top" }}
		>
			<Box sx={{ p: 2 }}>
				<Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
					<UserAvatar
						firstName={user?.firstName ?? null}
						lastName={user?.lastName ?? null}
						avatarUrl={user?.avatarUrl ?? null}
						size="large"
						data-testid="user-popover-avatar"
					/>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant="subtitle1" noWrap>
							{user?.firstName && user?.lastName
								? `${user.firstName} ${user.lastName}`
								: user?.email || "User"}
						</Typography>
						<Typography color="text.secondary" variant="body2" noWrap>
							{user?.email || ""}
						</Typography>
						{user?.userType && hasTier && tier ? (
							<RoleBadge
								role={user.userType}
								tier={tier}
								portal="humanda"
								size="small"
								sx={{ mt: 0.5 }}
								data-testid="user-popover-role-badge"
							/>
						) : null}
					</Box>
				</Stack>
				{user?.companyName ? (
					<Typography
						color="text.secondary"
						variant="body2"
						sx={{ mt: 1.5 }}
						data-testid="user-popover-company"
					>
						Organization: {user.companyName}
					</Typography>
				) : null}
				{hasTier && tier ? (
					<Typography
						color="text.secondary"
						variant="caption"
						sx={{ mt: 0.5, display: "block" }}
						data-testid="user-popover-tier"
					>
						{tier}
					</Typography>
				) : null}
			</Box>
			<Divider />
			<MenuList sx={{ p: 1 }}>
				<MenuItem
					component={RouterLink}
					href={paths.dashboard.account.profile}
					onClick={onClose}
				>
					<ListItemIcon>
						<UserIcon />
					</ListItemIcon>
					Profile
				</MenuItem>
			</MenuList>
			<Divider />
			<MenuList sx={{ p: 1 }}>
				<MenuItem onClick={handleSignOut}>
					<ListItemIcon>
						<SignOutIcon />
					</ListItemIcon>
					Sign out
				</MenuItem>
			</MenuList>
		</Popover>
	);
}
